import React, { useEffect } from "react";
import SEO from "../components/SEO";
import "./WhyAbacus.css";
import heroImg from "../assets/abacus1.jpg";
import infoImg from "../assets/why-abacus.jpg";

import AOS from "aos";
import "aos/dist/aos.css";

import {
  FaBrain,
  FaBolt,
  FaBullseye,
  FaSmile,
  FaChartLine,
  FaHeadphones,
} from "react-icons/fa";

function WhyAbacus() {
  useEffect(() => {
    AOS.init({
      duration: 1200,
      once: true,
      easing: "ease-out-cubic",
      offset: 100,
    });
  }, []);

  return (
    <>
    <SEO
      title="Why Learn Abacus? | Benefits of Abacus Training | SIP Abacus Malkajgiri"
      description="Discover why abacus training is essential for your child's development. SIP Abacus Malkajgiri explains how abacus improves concentration, memory, speed, confidence & analytical thinking. Learn the science behind mental arithmetic."
      keywords="why learn abacus, benefits of abacus, abacus benefits for children, brain training with abacus, mental arithmetic benefits, abacus for concentration, abacus for memory improvement, how abacus works, abacus and brain development, left brain right brain training, abacus advantage for students, mental math benefits, why abacus is important, abacus training benefits Hyderabad, abacus for confidence building, abacus speed and accuracy, analytical thinking for kids, SIP Abacus benefits Malkajgiri"
      canonicalPath="/why-abacus"
    />
    <section className="why-abacus-page">
      {/* Hero Section */}
      <section
        className="hero-section"
        style={{ backgroundImage: `url(${heroImg})` }}
      >
        <div className="hero-overlay">
          <div className="container hero-content">
            <h1 className="page-title" data-aos="fade-up">
              Why Abacus?
            </h1>

            <h2
              className="why-hero-subtitle"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              More than math it is powerful brain training for children
            </h2>

            <p
              className="hero-description"
              data-aos="fade-up"
              data-aos-delay="400"
            >
              Abacus learning helps children improve focus, memory, speed, and
              confidence while making math enjoyable and engaging.
            </p>
          </div>
        </div>
      </section>

      {/* Wave Divider */}
      <div className="wave-divider">
        <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
          <path
            d="M0,80 C300,140 1100,0 1440,80 L1440,120 L0,120 Z"
            className="wave-fill"
          ></path>
        </svg>
      </div>

      {/* What is Abacus */}
      <section className="info-section">
        <div className="container info-grid">
          <div className="info-image" data-aos="fade-right">
            <img src={infoImg} alt="Abacus tool" />
          </div>

          <div className="info-content" data-aos="fade-left">
            <h2 className="section-title left-align">What is Abacus?</h2>
            <p>
              The abacus is one of the oldest calculation tools used to perform
              arithmetic operations like addition, subtraction, multiplication,
              and division. It consists of beads arranged on rods, where each
              movement represents a number.
            </p>

            <p>
              More than just a math tool, abacus training activates both sides
              of the brain. It enhances concentration, memory, visualization
              skills, and problem solving ability in children making learning
              faster and more effective.
            </p>
          </div>
        </div>
      </section>

      {/* How Abacus Works */}
      <section className="how-section">
        <div className="container">
          <h2 className="section-title center" data-aos="fade-up">
            How Abacus Works
          </h2>

          <div className="flowchart">
            <div className="flow-step" data-aos="zoom-in" data-aos-delay="100">
              <div className="flow-box">
                <h3>Understanding the Abacus</h3>
                <p>
                  Children learn how numbers are represented using beads and
                  rods, understanding place values like ones, tens, and
                  hundreds.
                </p>
              </div>
            </div>

            <div className="flow-arrow"></div>

            <div className="flow-step" data-aos="zoom-in" data-aos-delay="200">
              <div className="flow-box">
                <h3>Hands on Practice</h3>
                <p>
                  Students perform calculations by physically moving beads,
                  making math visual and easy to grasp.
                </p>
              </div>
            </div>

            <div className="flow-arrow"></div>

            <div className="flow-step" data-aos="zoom-in" data-aos-delay="300">
              <div className="flow-box">
                <h3>Speed & Accuracy</h3>
                <p>
                  Regular practice improves calculation speed and reduces
                  mistakes, building confidence in children.
                </p>
              </div>
            </div>

            <div className="flow-arrow"></div>

            <div className="flow-step" data-aos="zoom-in" data-aos-delay="400">
              <div className="flow-box">
                <h3>Mental Visualization</h3>
                <p>
                  Eventually, children imagine the abacus in their mind and
                  solve problems mentally this is called mental abacus.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="benefits-section">
        <div className="container">
          <div className="benefits-heading" data-aos="fade-up">
            <span className="benefits-tag">
              Why children love learning with abacus
            </span>
            <h2 className="section-title center">
              Benefits of Learning Abacus
            </h2>
            <p className="benefits-subtext">
              Abacus training supports overall brain development and helps
              children become faster, sharper, and more confident learners in
              everyday life.
            </p>
          </div>

          <div className="benefits-grid">
            <div className="benefit-card" data-aos="flip-up" data-aos-delay="100">
              <div className="benefit-icon">
                <FaBullseye />
              </div>
              <h3>Improves Concentration</h3>
              <p>
                Children learn to stay focused for longer periods while solving
                problems with attention and care.
              </p>
            </div>

            <div className="benefit-card" data-aos="flip-up" data-aos-delay="200">
              <div className="benefit-icon">
                <FaBrain />
              </div>
              <h3>Boosts Memory Power</h3>
              <p>
                Regular abacus practice strengthens visual memory and helps
                children recall numbers quickly.
              </p>
            </div>

            <div className="benefit-card" data-aos="flip-up" data-aos-delay="300">
              <div className="benefit-icon">
                <FaBolt />
              </div>
              <h3>Enhances Speed & Accuracy</h3>
              <p>
                Students solve calculations faster and with better accuracy
                through repeated hands on and mental practice.
              </p>
            </div>

            <div className="benefit-card" data-aos="flip-up" data-aos-delay="400">
              <div className="benefit-icon">
                <FaSmile />
              </div>
              <h3>Develops Confidence</h3>
              <p>
                As children improve their skills, they feel more confident in
                math and in classroom participation.
              </p>
            </div>

            <div className="benefit-card" data-aos="flip-up" data-aos-delay="500">
              <div className="benefit-icon">
                <FaHeadphones />
              </div>
              <h3>Strengthens Listening Skills</h3>
              <p>
                Abacus sessions encourage attentive listening, helping children
                follow instructions carefully and effectively.
              </p>
            </div>

            <div className="benefit-card" data-aos="flip-up" data-aos-delay="600">
              <div className="benefit-icon">
                <FaChartLine />
              </div>
              <h3>Encourages Analytical Thinking</h3>
              <p>
                Children begin to understand patterns, logic, and problem solving
                in a much more structured way.
              </p>
            </div>
          </div>
        </div>
      </section>
    </section>
    </>
  );
}

export default WhyAbacus;